import React from "react";
import { Button as MuiButton, Tooltip, IconButton, Box } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import AnimatedLogo from "../assets/logos/animated-logo.svg?react";
import AnimatedLogoLight from "../assets/logos/animated-logo-light.svg?react";

// Styled animated logo for processing state
const ProcessingLogo = styled(Box)(({ theme }) => ({
  width: 32,
  height: 32,
  marginLeft: theme.spacing(1), // Changed from marginRight to marginLeft since it's now after text
  "& svg": {
    width: "100%",
    height: "100%",
  },
}));

// Styled contained button with modern hover effects
const StyledContainedButton = styled(MuiButton)(({ theme, processing }) => ({
  borderRadius: theme.spacing(1.25), // Slightly more rounded
  padding: theme.spacing(0.75, 2),
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  transition: "all 0.2s cubic-bezier(0.2, 0, 0.4, 1)", // More muted transition
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", // Subtle default shadow
  backgroundColor: processing
    ? theme.palette.grey[700] // Much darker grey when processing
    : theme.palette.primary.main,
  color: processing
    ? theme.palette.common.white // White text on dark grey background
    : theme.palette.mode === "dark"
      ? "#000000" // Black text in dark mode (primary color is too bright for white text)
      : theme.palette.text.primary, // Normal text color in light mode

  "&:hover": {
    backgroundColor: processing
      ? theme.palette.grey[700] // Stay dark grey when processing
      : theme.palette.mode === "dark"
        ? theme.palette.primary.light // Lighter on hover in dark mode
        : theme.palette.primary.dark, // Darker on hover in light mode (standard pattern)
    color: processing
      ? theme.palette.common.white // Keep white text when processing
      : theme.palette.mode === "dark"
        ? "#000000" // Keep black text in dark mode
        : theme.palette.text.primary, // Normal text color in light mode
    boxShadow: processing
      ? "0 2px 4px rgba(0, 0, 0, 0.1)" // Keep subtle shadow when processing
      : theme.palette.mode === "dark"
        ? `0 8px 24px ${theme.palette.primary.light}40, 0 4px 12px rgba(0, 0, 0, 0.15)` // Intense glow + depth in dark mode
        : `0 6px 16px ${theme.palette.primary.dark}20, 0 2px 8px rgba(0, 0, 0, 0.08)`, // Reduced intensity in light mode
    transform: processing ? "none" : "translateY(-1px) scale(1.01)", // No transform when processing
  },

  "&:active": {
    transform: processing ? "none" : "translateY(-0.5px) scale(1.005)", // No transform when processing
    backgroundColor: processing
      ? theme.palette.grey[700]
      : theme.palette.primary.main, // Back to main primary
    color: processing
      ? theme.palette.common.white
      : theme.palette.mode === "dark"
        ? "#000000" // Keep black text in dark mode
        : theme.palette.text.primary, // Normal text color in light mode
    boxShadow: processing
      ? "0 2px 4px rgba(0, 0, 0, 0.1)"
      : `0 3px 8px ${theme.palette.primary.main}40, 0 1px 4px rgba(0, 0, 0, 0.1)`, // Reduced shadow when pressed
    transition: "all 0.1s cubic-bezier(0.2, 0, 0.4, 1)", // Faster, more muted transition on press
  },

  "&:disabled": {
    opacity: 1, // Full opacity so the grey is very visible
    transform: "none",
    boxShadow: "none",
    backgroundColor: processing
      ? theme.palette.grey[700]
      : theme.palette.grey[400],
    color: processing ? theme.palette.common.white : theme.palette.grey[600],
  },

  // Ensure icon uses appropriate color for theme
  "& .MuiSvgIcon-root": {
    color: processing
      ? theme.palette.common.white
      : theme.palette.mode === "dark"
        ? "#000000" // Black icons in dark mode
        : theme.palette.text.primary, // Normal icon color in light mode
  },
}));

// Styled outlined button with border and matching text/icon colors
const StyledOutlinedButton = styled(MuiButton)(({ theme, processing }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  fontSize: "1rem",
  fontWeight: 500,
  textTransform: "none",
  transition: "all 0.2s ease-in-out",
  borderWidth: "2px",
  borderColor: processing
    ? theme.palette.grey[600]
    : theme.palette.primary.dark,
  color: processing ? theme.palette.grey[800] : theme.palette.text.primary,
  backgroundColor: processing
    ? theme.palette.grey[300] // More obvious grey background
    : theme.palette.mode === "dark"
      ? "transparent" // Transparent in dark mode
      : theme.palette.background.paper, // White background in light mode

  "&:hover": {
    borderWidth: "2px",
    borderColor: processing
      ? theme.palette.grey[600]
      : theme.palette.primary.dark,
    backgroundColor: processing
      ? theme.palette.grey[300] // Stay grey when processing
      : theme.palette.mode === "dark"
        ? `${theme.palette.primary.main}20` // More intense background in dark mode (20% opacity)
        : `${theme.palette.primary.main}14`, // 8% opacity primary on hover in light mode
    color: processing ? theme.palette.grey[800] : theme.palette.text.primary, // Maintain text color on hover
    transform: processing ? "none" : "translateY(-1px)",
    boxShadow: processing
      ? "none"
      : theme.palette.mode === "dark"
        ? `0 4px 12px ${theme.palette.primary.main}30` // Add glow in dark mode
        : "none", // No additional shadow in light mode
  },

  "&:active": {
    transform: processing ? "none" : "translateY(0)",
  },

  "&:disabled": {
    opacity: 1, // Full opacity so the grey is very visible
    transform: "none",
    backgroundColor: processing ? theme.palette.grey[300] : "transparent",
    color: processing ? theme.palette.grey[800] : theme.palette.grey[600],
  },

  // Ensure icon uses theme text color
  "& .MuiSvgIcon-root": {
    color: processing ? theme.palette.grey[800] : theme.palette.text.primary,
  },
}));

// Styled icon button for icon-only variant
const StyledIconButton = styled(IconButton)(({ theme, processing }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.75),
  transition: "all 0.2s ease-in-out",
  color: processing ? theme.palette.grey[600] : theme.palette.text.primary,

  "&:hover": {
    backgroundColor: processing
      ? "transparent"
      : theme.palette.mode === "dark"
        ? `${theme.palette.primary.main}25` // More intense background in dark mode (25% opacity)
        : `${theme.palette.primary.main}0D`, // Subtle background in light mode (5% opacity)
    transform: processing ? "none" : "translateY(-1px)",
    boxShadow: processing
      ? "none"
      : theme.palette.mode === "dark"
        ? `0 3px 8px ${theme.palette.primary.main}25` // Add subtle glow in dark mode
        : "none", // No additional shadow in light mode
  },

  "&:active": {
    transform: processing ? "none" : "translateY(0)",
  },

  "&:disabled": {
    opacity: 1, // Full opacity so the grey is very visible
    transform: "none",
  },
}));

const Button = ({
  variant = "contained",
  children,
  icon,
  tooltipText,
  disabled = false,
  processing = false,
  onClick,
  size = "medium",
  ...otherProps
}) => {
  const theme = useTheme();

  // Determine which animated logo to use based on theme
  const AnimatedLogoComponent =
    theme.palette.mode === "dark" ? AnimatedLogo : AnimatedLogoLight;

  // Create processing content with animated logo AFTER the text
  const processingContent = processing ? (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      Processing
      <ProcessingLogo>
        <AnimatedLogoComponent />
      </ProcessingLogo>
    </Box>
  ) : (
    children
  );

  // Button is disabled if explicitly disabled OR if processing
  const isDisabled = disabled || processing;

  // Icon-only variant with tooltip
  if (variant === "icon") {
    const tooltipContent =
      tooltipText ||
      `Click to ${children?.toLowerCase?.() || "perform action"}`;

    return (
      <Tooltip title={tooltipContent} arrow>
        <span>
          <StyledIconButton
            disabled={isDisabled}
            processing={processing}
            onClick={processing ? undefined : onClick}
            size={size}
            {...otherProps}>
            {processing ? <AnimatedLogoComponent /> : icon}
          </StyledIconButton>
        </span>
      </Tooltip>
    );
  }

  // Contained variant (normal button)
  if (variant === "contained") {
    return (
      <StyledContainedButton
        variant="contained"
        disabled={isDisabled}
        processing={processing}
        onClick={processing ? undefined : onClick}
        size={size}
        startIcon={processing ? null : icon}
        {...otherProps}>
        {processingContent}
      </StyledContainedButton>
    );
  }

  // Outlined variant (border only)
  if (variant === "outlined") {
    return (
      <StyledOutlinedButton
        variant="outlined"
        disabled={isDisabled}
        processing={processing}
        onClick={processing ? undefined : onClick}
        size={size}
        startIcon={processing ? null : icon}
        {...otherProps}>
        {processingContent}
      </StyledOutlinedButton>
    );
  }

  // Fallback to standard MUI button for any other variants
  return (
    <MuiButton
      variant={variant}
      color="primary"
      disabled={isDisabled}
      onClick={processing ? undefined : onClick}
      size={size}
      startIcon={processing ? null : icon}
      {...otherProps}>
      {processingContent}
    </MuiButton>
  );
};

export default Button;
